import React from 'react';
import Image from 'next/legacy/image';
import Link from 'components/CustomLink';
import classnames from 'classnames';

import shareHover from '../../../public/images/share.svg';
import share from '../../../public/images/share_white.svg';

import { getPropertyTypeByCode } from 'src/utils/getPropertyTypeByCode';

import { getSmallImageUrlFromImage } from 'lib/getImages';
import { defaultImage } from 'src/constants/constants';

import { FiltersValues } from 'components/UnitsFilters/types';

import ThreeDotsV2 from './ThreeDotsV2';

import SaveToFavoritesButton from 'components/SavedHouses/SaveToFavoritesButton';
import { Listing } from 'apolloClient/types/Units';
import { useAuth } from 'components/Auth/AuthProvider';
import { EntityWithId, Building } from 'apolloClient/types';
import { isLoginAsUser } from 'src/utils/authUtils';
import { getHrefForUnitLink } from 'components/Search/utils/listingsFiltersUtils';
import { flattenStrapiDataItem } from 'lib/flattenStrapiBulkDataItems';
import PopUpSearchQuickView, {
  tabsCollectionKeys,
} from 'components/PopUpSearchQuickView';
import {
  centenaryNumberFormatter,
  getFormattedPriceChangeInDollars,
  numberFormatter,
  priceFormatter,
} from 'src/utils/utils';
import Share from 'components/Share';
import { ORIGIN } from 'src/constants';
import { translateUnitStatus } from 'lib/translateUnitStatus';
import BathsCount from 'components/BathsCount';
import { getListingDisplayPrice } from 'src/utils/listing';

export type CondoCardV2Props = {
  className?: string;
  listing: Listing;
  index: number;
  showThreeDots?: boolean;
  mediumHeight?: boolean;
  largeHeight?: boolean;
  smallHeight?: boolean;
  showDeltaPrice?: boolean;
  showWordingForDeltaPrice?: boolean;
  isPPC?: boolean;
  isIconPPC?: boolean;
  imageClassName?: string;
  items?: EntityWithId<Listing>[];
  hoveredBuilding?: Building | null;
  setHoveredBuilding?: (building: Building | null) => void;
  fromMyFavorites?: boolean;
  carouselResponsivness?: boolean;
};

const CondoCardV2: React.FC<CondoCardV2Props> = ({
  className,
  isIconPPC,
  isPPC,
  listing,
  index,
  items,
  showThreeDots = false,
  largeHeight = false,
  mediumHeight = false,
  smallHeight = false,
  showDeltaPrice = false,
  showWordingForDeltaPrice = true,
  imageClassName,
  setHoveredBuilding,
  fromMyFavorites,
  carouselResponsivness = false,
}) => {
  const href = getHrefForUnitLink(listing);
  const unit = flattenStrapiDataItem(listing?.unit?.data);
  const building = flattenStrapiDataItem(unit?.building?.data);
  const neighborhood = flattenStrapiDataItem(building?.neighborhood?.data);

  const { images } = listing || {};

  function filterTabs(): tabsCollectionKeys[] {
    const tabs: tabsCollectionKeys[] = ['photos'];
    listing.hasFloorplan && tabs.push('floorplan');
    building.lat && building.lon && tabs.push('direction');
    listing.virtualTourUrl && tabs.push('tour');
    return tabs;
  }

  function renderPriceDelta() {
    if (
      listing.statusCode === 'CS' ||
      listing.statusCode === 'R' ||
      !listing.previousPrice ||
      listing.previousPrice <= 0 ||
      listing.price === listing.previousPrice ||
      listing.price / listing.previousPrice >= 3 ||
      !+centenaryNumberFormatter(
        ((listing.price - listing.previousPrice) * 100.0) /
          listing.previousPrice
      )
    )
      return null;

    const delta = getFormattedPriceChangeInDollars(
      listing.price,
      listing.previousPrice
    );

    return (
      <div
        title={`Was ${priceFormatter(listing.previousPrice)}`}
        className={`text-16-26-0.3 ${
          delta.includes('-') ? 'text-red' : 'text-azure'
        } font-sohne`}
      >
        {showWordingForDeltaPrice
          ? `Price ${delta.includes('-') ? 'Reduced' : 'Increased'}: `
          : ''}
        {delta}
      </div>
    );
  }
  const { me } = useAuth();
  const parcedPrice = getListingDisplayPrice(listing);

  return (
    <div className={className}>
      <div className="relative w-full">
        <div>
          <div
            style={
              carouselResponsivness
                ? { height: 0, paddingBottom: '70%' }
                : undefined
            }
            className={classnames(
              `${imageClassName} relative h-[190px] md:h-[183px] lg:h-[183px]`,
              { 'lg:h-72 xl:h-[256px]': largeHeight },
              { 'h-56 xl:h-[256px]': mediumHeight },
              { 'h-40 xl:h-[256px]': smallHeight }
            )}
          >
            <Link
              target={fromMyFavorites && isLoginAsUser() ? '_self' : '_blank'}
              href={href}
              passHref
              className="cursor-pointer image-wrapper"
              rel="nofollow"
              onMouseEnter={() =>
                setHoveredBuilding && setHoveredBuilding(building)
              }
              onMouseLeave={() =>
                setHoveredBuilding && setHoveredBuilding(null)
              }
            >
              <Image
                className="object-cover"
                layout="fill"
                src={
                  images?.data?.[0]?.attributes?.url
                    ? getSmallImageUrlFromImage(images?.data?.[0]?.attributes)
                    : defaultImage
                }
                blurDataURL={defaultImage}
                placeholder="blur"
                loading="eager"
                alt={`${building.name} #${listing.unitNumber}`}
              />
            </Link>
            <div className="absolute bottom-0 w-full h-12 space-x-2 shadow-top" />
            <div className="absolute bottom-0 flex items-center justify-end w-full h-12 pt-1  right-[14px]">
              {(me?.id || (!isPPC && !isIconPPC)) && (
                <Share
                  copyData={`${ORIGIN}${href}`}
                  renderButton={() => (
                    <button className="cursor-pointer group px-2">
                      <div className="group-hover:hidden">
                        <Image src={share} alt="share" height={25} width={25} />
                      </div>
                      <div className="hidden group-hover:block">
                        <Image
                          src={shareHover}
                          alt="share"
                          height={25}
                          width={25}
                        />
                      </div>
                    </button>
                  )}
                />
              )}
              {!!items?.length && (
                <PopUpSearchQuickView
                  isIconPPC={isIconPPC}
                  isPPC={isPPC}
                  items={items}
                  listing={listing}
                  activeUnitIndex={index}
                  tabsForRender={filterTabs()}
                  btnsClassName="px-[8px]"
                />
              )}
              <SaveToFavoritesButton
                type={getPropertyTypeByCode(listing.propertyTypeCode)}
                unitId={unit.id}
                userFavorites={unit.favorite}
                className="px-[8px]"
              />
              <ThreeDotsV2
                showThreeDots={showThreeDots}
                listing={listing}
                building={building}
                className="pl-[8px]"
                unitStatus={translateUnitStatus(
                  listing.propertyTypeCode,
                  listing.statusCode,
                  listing.closeDate,
                  listing.isReo,
                  listing.isShortSale,
                  building.isPreconstruction,
                  building.yearBuilt
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <Link href={href} passHref target="_blank">
        <div className="pt-[12px]">
          <div className="flex flex-col">
            <div className="text-16-26-0.3 uppercase font-sohneKraftig text-gold-darker p-[1px]">
              <div className="text-gold-darker">
                {translateUnitStatus(
                  listing.propertyTypeCode,
                  listing.statusCode,
                  listing.closeDate,
                  listing.isReo,
                  listing.isShortSale,
                  building.isPreconstruction,
                  listing.yearBuilt
                )}
              </div>
            </div>
            <div className="flex items-center justify-start mt-[3px] text-16-26-0.3 font-sohneKraftig lg:text-20-28-0.3">
              <div className="text-20-28-0.3 ">
                {priceFormatter(parcedPrice)}
              </div>
              {showDeltaPrice && (
                <div className="text-20-28-0.3 ml-[10px]">
                  {renderPriceDelta()}
                </div>
              )}
            </div>
            <div className="">
              <div className="flex text-14-26-0.3 text-gray lg:text-16-26-0.3">
                <div className="pr-[10px] mr-[8px] border-r-[2px] border-gray text-16-26-0.3">
                  {listing.sqft ? numberFormatter(listing.sqft) : 0} sq ft
                </div>
                <div className="text-16-26-0.3">
                  {listing.bedsTotal === null ? '0' : listing.bedsTotal} beds,{' '}
                  <BathsCount listing={listing} fallback={0} /> baths
                </div>
              </div>
              <div className="mt-[3px] mb-[6px] md:my-1 text-13-18-0.3 text-black lg:text-16-26-0.3 font-sohneKraftig">
                <h3 className="text-13-18-0.3 uppercase">
                  {building?.name || 'No name'} #{unit.unique}
                </h3>
                <div className="text-13-18-0.3">{neighborhood?.name}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="h-px mt-[6px] md:mt-[8px] xl:mt-[10px] w-fill bg-beige" />
      </Link>
    </div>
  );
};

export default CondoCardV2;
